import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { productAPI } from '../../services/api';
import ProductCard from '../../components/shop/ProductCard';

const CATEGORIES = [
  { id: 'floral', label: 'Floral', desc: 'Rose, Jasmine, Lily', emoji: '🌹' },
  { id: 'woody', label: 'Woody', desc: 'Sandalwood, Cedar', emoji: '🪵' },
  { id: 'oriental', label: 'Oriental', desc: 'Amber, Musk, Vanilla', emoji: '✨' },
  { id: 'fresh', label: 'Fresh', desc: 'Ocean, Green, Aquatic', emoji: '🌿' },
  { id: 'citrus', label: 'Citrus', desc: 'Lemon, Bergamot, Orange', emoji: '🍋' },
  { id: 'gourmand', label: 'Gourmand', desc: 'Caramel, Coffee, Cocoa', emoji: '🍂' },
];

const HomePage = () => {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    productAPI.getFeatured()
      .then(({ data }) => setFeatured(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section style={{ position: 'relative', height: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        {/* Animated background */}
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 60% 50%, rgba(201,168,76,0.08) 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(201,168,76,0.05) 0%, transparent 50%)', zIndex: 0 }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://images.unsplash.com/photo-1541643600914-78b084683702?w=1600&q=80)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.2, zIndex: 0 }} />

        {/* Floating orbs */}
        <div style={{ position: 'absolute', top: '20%', right: '15%', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%)', animation: 'float 6s ease-in-out infinite', zIndex: 0 }} />
        <div style={{ position: 'absolute', bottom: '20%', left: '10%', width: '200px', height: '200px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.04) 0%, transparent 70%)', animation: 'float 8s ease-in-out infinite 2s', zIndex: 0 }} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ maxWidth: '620px' }}>
            <p style={{ fontSize: '11px', letterSpacing: '0.35em', color: 'var(--gold)', marginBottom: '24px', fontWeight: 500, animation: 'fadeUp 0.6s ease forwards' }}>
              THE ART OF FRAGRANCE
            </p>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(48px, 7vw, 88px)', lineHeight: 1.05, fontWeight: 400, marginBottom: '24px', animation: 'fadeUp 0.6s ease 0.1s both' }}>
              Discover Your<br />
              <span style={{ color: 'var(--gold)', fontStyle: 'italic' }}>Signature</span> Scent
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--white-dim)', lineHeight: 1.8, maxWidth: '440px', marginBottom: '40px', letterSpacing: '0.03em', animation: 'fadeUp 0.6s ease 0.2s both' }}>
              Handcrafted luxury fragrances that tell your unique story. Each bottle is a masterpiece of olfactory art.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', animation: 'fadeUp 0.6s ease 0.3s both' }}>
              <Link to="/collection" style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                background: 'var(--gold)', color: 'var(--black)', padding: '14px 32px',
                fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase',
                transition: 'background 0.3s, transform 0.3s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--gold-light)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--gold)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >Explore Collection <ArrowRight size={14} /></Link>
              <Link to="/ai-advisor" style={{
                display: 'inline-flex', alignItems: 'center', gap: '10px',
                background: 'transparent', color: 'var(--white)', padding: '14px 32px',
                border: '1px solid var(--border-hover)', fontSize: '11px', fontWeight: 500, letterSpacing: '0.2em',
                transition: 'border-color 0.3s, transform 0.3s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.color = 'var(--gold)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-hover)'; e.currentTarget.style.color = 'var(--white)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              ><Sparkles size={14} /> AI Advisor</Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{ position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <p style={{ fontSize: '9px', letterSpacing: '0.3em', color: 'var(--white-dim)' }}>SCROLL</p>
          <div style={{ width: '1px', height: '48px', background: 'linear-gradient(to bottom, var(--gold), transparent)', animation: 'pulse 2s ease infinite' }} />
        </div>
      </section>

      {/* Featured Collection */}
      <section className="section" style={{ background: 'var(--black)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <p style={{ fontSize: '10px', letterSpacing: '0.3em', color: 'var(--gold)', marginBottom: '16px' }}>CURATED FOR YOU</p>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 400 }}>Featured Collection</h2>
          </div>

          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '24px' }}>
              {[...Array(4)].map((_, i) => (
                <div key={i} className="skeleton" style={{ height: '380px' }} />
              ))}
            </div>
          ) : featured.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '24px' }}>
              {featured.slice(0, 8).map(p => <ProductCard key={p._id} product={p} />)}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--white-dim)' }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: '24px', marginBottom: '12px' }}>Collection Coming Soon</p>
              <p style={{ fontSize: '13px' }}>Check back shortly as we curate our finest selections.</p>
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/collection" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '11px', letterSpacing: '0.2em', color: 'var(--gold)', borderBottom: '1px solid var(--gold)', paddingBottom: '4px', transition: 'gap 0.3s' }}
              onMouseEnter={e => e.currentTarget.style.gap = '16px'}
              onMouseLeave={e => e.currentTarget.style.gap = '8px'}
            >VIEW ALL <ArrowRight size={12} /></Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="section" style={{ background: 'var(--dark)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <p style={{ fontSize: '10px', letterSpacing: '0.3em', color: 'var(--gold)', marginBottom: '16px' }}>EXPLORE BY FAMILY</p>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 400 }}>Scent Families</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '16px' }}>
            {CATEGORIES.map(cat => (
              <Link key={cat.id} to={`/collection?category=${cat.id}`}
                style={{ display: 'block', background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '32px 24px', textAlign: 'center', transition: 'all 0.3s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.background = 'var(--dark-3)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = 'var(--dark-2)'; }}
              >
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>{cat.emoji}</div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', marginBottom: '6px' }}>{cat.label}</h3>
                <p style={{ fontSize: '11px', color: 'var(--white-dim)', letterSpacing: '0.05em' }}>{cat.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AI Banner */}
      <section className="section" style={{ background: 'var(--black)' }}>
        <div className="container">
          <div style={{ border: '1px solid var(--border)', padding: '64px', textAlign: 'center', background: 'linear-gradient(135deg, rgba(201,168,76,0.04) 0%, transparent 50%, rgba(201,168,76,0.04) 100%)', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%)' }} />
            <Sparkles size={32} color="var(--gold)" style={{ marginBottom: '24px' }} />
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 4vw, 48px)', marginBottom: '16px', fontWeight: 400 }}>
              Let AI Find Your <span style={{ color: 'var(--gold)', fontStyle: 'italic' }}>Perfect Scent</span>
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--white-dim)', maxWidth: '500px', margin: '0 auto 32px', lineHeight: 1.8 }}>
              Our AI fragrance advisor analyzes your preferences, mood, and occasion to find the fragrance that speaks to you.
            </p>
            <Link to="/ai-advisor" style={{
              display: 'inline-flex', alignItems: 'center', gap: '10px',
              background: 'var(--gold)', color: 'var(--black)', padding: '14px 40px',
              fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em',
              transition: 'all 0.3s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--gold-light)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--gold)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >Try AI Advisor <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
